// Deterministic generative fallback art for post cards ("composition study").
// System: a Kandinsky-flavored geometric abstraction — warm ivory ground, two
// soft accent shades beneath, then 4-6 seeded objects from a fixed vocabulary
// (concentric target, haloed circle, triangle, arc, line bundle, wedge, dot
// grid) placed on jittered grid slots along a unifying diagonal axis line,
// finished with fine film grain. Everything derives from a string seed (the
// task SK), so the same post always renders the same image. No dependencies;
// pure canvas 2D so it runs in the card and exports as PNG.

const KANDINSKY_BASE = {
    ground: "#f2e9d8",
    ink: "#26241f",
    accents: ["#d2392e", "#1c448c", "#e8b830", "#3e8e7e", "#8a4b7c"],
};

// Same art schema on both domains; the brand color joins the accent pool so
// the pieces still read as "ours" (pink/orange on growth.cool, blue on
// 1stinfluencer).
export const PALETTES = {
    growth: {
        ...KANDINSKY_BASE,
        accents: KANDINSKY_BASE.accents.concat(["#fe60a1"]),
    },
    firstinfluencer: {
        ...KANDINSKY_BASE,
        accents: KANDINSKY_BASE.accents.concat(["#0074e4"]),
    },
};

export function paletteForHostname(hostname) {
    return hostname && hostname.includes("1stinfluencer")
        ? PALETTES.firstinfluencer
        : PALETTES.growth;
}

// FNV-1a 32-bit
export function hashSeed(str) {
    let h = 0x811c9dc5;
    const s = String(str || "post");
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}

export function mulberry32(seed) {
    let a = seed >>> 0;
    return function () {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

export const OBJECT_TYPES = ["target", "circle", "triangle", "arc", "lines", "wedge", "grid"];

// All layout is in unit space (0..1) so one param set renders at any size.
export function derivePostArtParams(seedString, palette) {
    const rand = mulberry32(hashSeed(seedString));
    const p = palette || PALETTES.growth;
    const pick = (arr) => arr[Math.floor(rand() * arr.length)];

    // Two soft under-shades so the ground isn't flat (the "shades" layer).
    const shades = [];
    for (let i = 0; i < 2; i++) {
        shades.push({
            x: 0.15 + rand() * 0.7,
            y: 0.15 + rand() * 0.7,
            r: 0.35 + rand() * 0.3,
            color: pick(p.accents),
            alpha: 0.07 + rand() * 0.08,
        });
    }

    // Unifying diagonal axis line.
    const axis = {
        rising: rand() < 0.5,
        offset: (rand() - 0.5) * 0.3, // perpendicular shift of the diagonal
        width: 0.004 + rand() * 0.004,
        alpha: 0.5 + rand() * 0.3,
    };

    // 4-6 objects on shuffled, jittered slots of a 3x2 grid. The first object
    // is dominant; the rest support it.
    const slots = [
        [0.2, 0.28], [0.5, 0.28], [0.8, 0.28],
        [0.2, 0.72], [0.5, 0.72], [0.8, 0.72],
    ];
    for (let i = slots.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        const tmp = slots[i]; slots[i] = slots[j]; slots[j] = tmp;
    }
    const objectCount = 4 + Math.floor(rand() * 3); // 4..6
    const objects = [];
    for (let i = 0; i < objectCount; i++) {
        const slot = slots[i];
        const dominant = i === 0;
        objects.push({
            type: pick(OBJECT_TYPES),
            x: slot[0] + (rand() - 0.5) * 0.12,
            y: slot[1] + (rand() - 0.5) * 0.12,
            size: dominant ? 0.2 + rand() * 0.1 : 0.07 + rand() * 0.1, // of long side
            rotation: rand() * Math.PI * 2,
            colorA: pick(p.accents),
            colorB: pick(p.accents),
            filled: rand() < 0.65,
            rings: 2 + Math.floor(rand() * 3), // target only: 2..4
            count: 2 + Math.floor(rand() * 2), // lines bundle: 2..3
            span: 0.9 + rand() * 1.6, // arc/wedge angular span (radians)
        });
    }

    return { shades, axis, objects, grain: 0.045 };
}

export function renderPostArt(canvas, seedString, opts) {
    const options = opts || {};
    const palette = options.palette || PALETTES.growth;
    const params = derivePostArtParams(seedString, palette);
    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;
    const long = Math.max(w, h);

    // 1. Ground
    ctx.fillStyle = palette.ground;
    ctx.fillRect(0, 0, w, h);

    // 2. Soft under-shades
    for (let i = 0; i < params.shades.length; i++) {
        const s = params.shades[i];
        const r = s.r * long;
        const g = ctx.createRadialGradient(s.x * w, s.y * h, 0, s.x * w, s.y * h, r);
        g.addColorStop(0, withAlpha(s.color, s.alpha));
        g.addColorStop(1, withAlpha(s.color, 0));
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
    }

    // 3. Axis line (behind the objects)
    const ax = params.axis;
    ctx.strokeStyle = withAlpha(palette.ink, ax.alpha);
    ctx.lineWidth = Math.max(1, ax.width * long);
    ctx.beginPath();
    if (ax.rising) {
        ctx.moveTo(-0.05 * w, (1.05 + ax.offset) * h);
        ctx.lineTo(1.05 * w, (-0.05 + ax.offset) * h);
    } else {
        ctx.moveTo(-0.05 * w, (-0.05 + ax.offset) * h);
        ctx.lineTo(1.05 * w, (1.05 + ax.offset) * h);
    }
    ctx.stroke();

    // 4. Objects
    for (let i = 0; i < params.objects.length; i++) {
        drawObject(ctx, params.objects[i], w, h, long, palette);
    }

    // 5. Seeded film grain (own PRNG stream so layout stays stable if tuned)
    const grainRand = mulberry32(hashSeed(seedString) ^ 0x9e3779b9);
    const img = ctx.getImageData(0, 0, w, h);
    const d = img.data;
    const amp = params.grain * 255;
    for (let i = 0; i < d.length; i += 4) {
        const n = (grainRand() - 0.5) * amp;
        d[i] += n;
        d[i + 1] += n;
        d[i + 2] += n;
    }
    ctx.putImageData(img, 0, 0);
}

function drawObject(ctx, o, w, h, long, palette) {
    const cx = o.x * w;
    const cy = o.y * h;
    const r = o.size * long;
    const inkLine = Math.max(1, 0.004 * long);

    ctx.save();
    ctx.translate(cx, cy);

    if (o.type === "target") {
        // Concentric rings, alternating accents, ink outer outline.
        for (let i = o.rings; i >= 1; i--) {
            ctx.fillStyle = i % 2 === 0 ? o.colorA : o.colorB;
            ctx.beginPath();
            ctx.arc(0, 0, (r * i) / o.rings, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.strokeStyle = palette.ink;
        ctx.lineWidth = inkLine;
        ctx.beginPath();
        ctx.arc(0, 0, r, 0, Math.PI * 2);
        ctx.stroke();
    } else if (o.type === "circle") {
        // Solid circle with an offset thin ink halo.
        ctx.fillStyle = withAlpha(o.colorA, 0.92);
        ctx.beginPath();
        ctx.arc(0, 0, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = palette.ink;
        ctx.lineWidth = inkLine;
        ctx.beginPath();
        ctx.arc(r * 0.12, -r * 0.1, r * 1.12, 0, Math.PI * 2);
        ctx.stroke();
    } else if (o.type === "triangle") {
        ctx.rotate(o.rotation);
        ctx.beginPath();
        ctx.moveTo(0, -r);
        ctx.lineTo(r * 0.9, r * 0.7);
        ctx.lineTo(-r * 0.9, r * 0.7);
        ctx.closePath();
        if (o.filled) {
            ctx.fillStyle = withAlpha(o.colorA, 0.9);
            ctx.fill();
        }
        ctx.strokeStyle = palette.ink;
        ctx.lineWidth = inkLine;
        ctx.stroke();
    } else if (o.type === "arc") {
        // Thick open arc segment.
        ctx.rotate(o.rotation);
        ctx.strokeStyle = withAlpha(o.colorA, 0.9);
        ctx.lineWidth = Math.max(2, r * 0.28);
        ctx.lineCap = "butt";
        ctx.beginPath();
        ctx.arc(0, 0, r, 0, o.span);
        ctx.stroke();
    } else if (o.type === "lines") {
        // Bundle of 2-3 parallel rules.
        ctx.rotate(o.rotation);
        ctx.strokeStyle = withAlpha(o.colorA, 0.85);
        ctx.lineWidth = Math.max(1, r * 0.08);
        const gap = r * 0.3;
        for (let i = 0; i < o.count; i++) {
            const off = (i - (o.count - 1) / 2) * gap;
            ctx.beginPath();
            ctx.moveTo(-r * 1.4, off);
            ctx.lineTo(r * 1.4, off);
            ctx.stroke();
        }
    } else if (o.type === "wedge") {
        // Filled circle sector.
        ctx.rotate(o.rotation);
        ctx.fillStyle = withAlpha(o.colorA, 0.88);
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.arc(0, 0, r, 0, o.span);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = palette.ink;
        ctx.lineWidth = inkLine;
        ctx.stroke();
    } else {
        // "grid": 3x3 patch of dots, alternating two accents.
        const step = (r * 2) / 3;
        const dot = Math.max(1.5, r * 0.16);
        for (let gy = 0; gy < 3; gy++) {
            for (let gx = 0; gx < 3; gx++) {
                ctx.fillStyle = (gx + gy) % 2 === 0 ? o.colorA : o.colorB;
                ctx.beginPath();
                ctx.arc(-r + step * gx + step / 2, -r + step * gy + step / 2, dot, 0, Math.PI * 2);
                ctx.fill();
            }
        }
    }

    ctx.restore();
}

function withAlpha(hex, alpha) {
    const v = hex.replace("#", "");
    const r = parseInt(v.substring(0, 2), 16);
    const g = parseInt(v.substring(2, 4), 16);
    const b = parseInt(v.substring(4, 6), 16);
    return "rgba(" + r + "," + g + "," + b + "," + alpha.toFixed(3) + ")";
}
